import { Link, Navigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import {
  CLUB_PLAN_MONTHLY_PRICING,
  TierFeatureKey,
  buildOrgEntitlements,
  hasAcademyManagementRole,
  hasEntitlement,
  type OrgEntitlements,
} from '@court-sync/shared';
import { useAuthStore } from '@/store/auth';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { Button } from '@/components/ui';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';

const SETTINGS_SUBSCRIPTION_HREF = '/settings?tab=subscription';

export interface ClubFeatureGateProps {
  /** When set, checks org module entitlement for this club feature key. */
  feature?: TierFeatureKey;
  children: React.ReactNode;
  title?: string;
  description?: string;
  /**
   * When true, academy operators without the club module see nothing instead of
   * the upsell or a redirect (for embedded academy surfaces).
   */
  silent?: boolean;
}

/**
 * Gates club module surfaces when the organization lacks club_management
 * or a specific club module feature entitlement.
 */
export function ClubFeatureGate({
  feature,
  children,
  title = 'Club management module required',
  description,
  silent = false,
}: ClubFeatureGateProps) {
  const { user } = useAuthStore();
  const { entitlements, hasClubManagement, isLoading } = useClubModuleAccess();

  if (isLoading) {
    return (
      <div className="flex min-h-[160px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  const orgEntitlements: OrgEntitlements =
    entitlements ?? buildOrgEntitlements({ clubManagement: false, tier: 'starter' });
  const hasAccess =
    feature != null
      ? hasEntitlement(orgEntitlements, feature)
      : hasClubManagement;

  if (!hasAccess) {
    if (hasAcademyManagementRole(user)) {
      return silent ? null : <Navigate to="/admin/dashboard" replace />;
    }

    const priceHint = `from $${CLUB_PLAN_MONTHLY_PRICING.USD}/mo`;
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={Lock} />
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400">
          Club module
        </p>
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="mx-auto mb-6 max-w-md text-gray-500 dark:text-gray-400">
          {description ??
            `Add a Club plan (${priceHint}) to manage court hire, memberships, inventory, and facility operations.`}
        </p>
        <Link to={SETTINGS_SUBSCRIPTION_HREF}>
          <Button size="lg">View subscription</Button>
        </Link>
      </DashboardEmptyStateCard>
    );
  }

  return <>{children}</>;
}
